import classNames from 'classnames';

import { clinicsTexts } from '@constant/clinics';
import { GetServicesCarouselDetailItem } from '@models/clinics';
import Icon from '@utilities/icon';
import { colorPallete } from '@configs/color-pallete';
import NextImage from '@com/_core/NextImage';
import Divider from '@com/_atoms/Divider';
import { convertRialToTomanNumber } from '@utilities/mainUtils';
import { GetUserServiceByDoctorIdItemModel } from '@models/profile';

type ServiceCardProps = {
  item: GetServicesCarouselDetailItem | GetUserServiceByDoctorIdItemModel;
  onClick: () => void;
  containerClassname?: string;
  dataTestId?: string;
};

const ServiceCard = ({
  item,
  containerClassname,
  onClick,
  dataTestId: dataTestId,
}: ServiceCardProps) => {
  const imageLink =
    (item as GetServicesCarouselDetailItem)?.imageLink ??
    (item as GetUserServiceByDoctorIdItemModel)?.imageURL;

  return (
    <div
      onClick={onClick}
      className={classNames(
        'w-[204px] h-[320px] flex flex-col bg-surface-primary shadow-fab px-3 py-2 rounded-lg cursor-pointer relative',
        containerClassname,
      )}
      data-testid={dataTestId}
    >
      <div className="absolute top-[4px] right-[4px] z-10 bg-surface-accent text-content-onInverse text-Label/xSmall flex items-center gap-x-1 px-2 rounded-full w-max h-5">
        <Icon
          name="Card"
          width={0.75}
          height={0.75}
          fill={colorPallete.content.onInverse}
        />
        <span>{clinicsTexts.installment}</span>
      </div>

      <div className="flex items-center justify-center w-[180px] h-[104px] min-h-[104px] rounded-lg bg-surface-secondary overflow-hidden mt-1">
        <NextImage
          src={imageLink}
          alt="clinics-services"
          width={180}
          height={104}
          imageClassName="min-h-[104px]"
        />
      </div>

      <span className="h-12 text-Label/Small text-content-primary mt-2 mb-auto line-clamp-2">
        {(item as GetUserServiceByDoctorIdItemModel).serviceHierarchyName ||
          (item as GetServicesCarouselDetailItem).detailName}
      </span>

      {item.description && (
        <div className={classNames('flex items-center gap-x-2')}>
          <Icon
            name="Stethoscope"
            width={1}
            height={1}
            fill={colorPallete.content.tertiary}
            className="min-w-4"
          />

          <span className="text-Body/xSmall text-content-tertiary line-clamp-2">
            {item.description}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        {!!(item as GetServicesCarouselDetailItem)?.city && (
          <div className="flex items-center gap-x-2">
            <Icon
              name="PinCircle"
              width={1}
              height={1}
              fill={colorPallete.content.primary}
              className="min-w-4"
            />

            <span className="text-Label/xSmall text-content-primary">
              {(item as GetServicesCarouselDetailItem)?.city?.join(', ')}
            </span>
          </div>
        )}

        {!!(item as GetServicesCarouselDetailItem)?.rate && (
          <div className="flex items-center gap-x-1">
            <span className="text-content-secondary text-Label/xSmall flex items-center gap-x-1">
              <span className="text-Label/2xSmall text-content-tertiary">
                {!!(item as GetServicesCarouselDetailItem)?.rate
                  ? !!(item as GetServicesCarouselDetailItem)?.reviewCount
                    ? `(از ${(item as GetServicesCarouselDetailItem)?.reviewCount} نظر)`
                    : 'امتیاز پزشک'
                  : 'بدون نظر'}
              </span>

              {!!(item as GetServicesCarouselDetailItem)?.rate}
            </span>

            <Icon
              name="StarFill"
              width={0.75}
              height={0.75}
              fill={colorPallete.content.warning}
            />
          </div>
        )}
      </div>

      <div className="mt-2">
        <Divider className="h-[1px] mt-2 ml-6" padding={0} />
      </div>

      <div
        className={classNames(
          'mt-2 flex items-center',
          item?.discountPercent ? 'justify-between' : 'justify-end',
        )}
      >
        {!(item as GetUserServiceByDoctorIdItemModel)?.deposit &&
          !!item?.discountPercent && (
            <div className="flex items-center gap-x-1">
              <span className="text-content-tertiary text-Body/xSmall line-through">
                {convertRialToTomanNumber(item?.price).toLocaleString('fa-IR')}
              </span>

              <div className="bg-surface-Gradient.brand text-Label/2xSmall text-content-onInverse rounded-full h-5 px-1.5 py-0.5">
                {item?.discountPercent}٪
              </div>
            </div>
          )}

        <div className="flex items-center gap-x-1 text-Label/xSmall text-content-primary">
          {!!(item as GetUserServiceByDoctorIdItemModel)?.deposit ||
          item?.price ? (
            <>
              <span>
                {!!(item as GetUserServiceByDoctorIdItemModel)?.deposit &&
                  'بیعانه '}
                {convertRialToTomanNumber(
                  !!(item as GetUserServiceByDoctorIdItemModel)?.deposit
                    ? (item as GetUserServiceByDoctorIdItemModel)?.deposit
                    : (item?.discountPrice ?? item?.price),
                ).toLocaleString('fa-IR')}
              </span>
              <span>تومان</span>
            </>
          ) : (
            <span>پرداخت در مطب</span>
          )}
        </div>
      </div>

      {!!(item as GetServicesCarouselDetailItem)?.slangName?.length &&
        (item as GetServicesCarouselDetailItem)?.slangName?.map(
          (slang, index) => (
            <div
              key={index}
              className="bg-surface-disable text-Label/2xSmall text-content-tertiary flex items-center gap-x-0.5 px-1.5 rounded-full w-max my-1"
            >
              <span>{slang}</span>
            </div>
          ),
        )}
    </div>
  );
};

export default ServiceCard;
